import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSourcingCompanyNews } from '../../../services/queries/SourcingCompanyQueries';
import { ICompanyNewsItem } from '../../../schemas/CompanyProfile.schema';
import { resolveCompanyLookup } from './resolveCompanyLookup';

export const SOURCING_COMPANY_NEWS_QUERY_KEY = 'sourcing-company-news';

// News changes more frequently than the merged profile, but each request is
// cheap and the visible payload is tiny. Five minutes balances freshness
// against re-renders when the user navigates between profile sections.
const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Fetches recent news items for the sourcing profile page. Mirrors the
 * lookup contract used by `useSourcingProfile` so the panel can load
 * independently of the merged profile (and degrade gracefully when the
 * news endpoint is unavailable).
 */
export function useSourcingCompanyNews(companyId: string) {
  const lookup = useMemo(() => resolveCompanyLookup(companyId), [companyId]);
  const hasLookup = Object.keys(lookup).length > 0;

  return useQuery<readonly ICompanyNewsItem[]>({
    queryKey: [SOURCING_COMPANY_NEWS_QUERY_KEY, companyId],
    queryFn: async ({ signal }) => {
      const response = await fetchSourcingCompanyNews(lookup, signal);
      return response.news;
    },
    staleTime: FIVE_MINUTES,
    enabled: Boolean(companyId) && hasLookup,
  });
}
